import { Controller } from "@hotwired/stimulus"

const RECIPE_FILTER_KEY = "ideas-plan:recipe-filter"
const RECIPE_FILTER_ATTRIBUTE = "data-ideas-plan-recipe-filter-value"

// How many cards a rail section shows before it offers the rest. Enough that
// most sessions never press the button, few enough that the rail's scrollbar
// means something on a backlog of two hundred.
const PAGE = 25

// The Ideas planner board: backlog rail filtering, and drag to schedule.
//
// Native HTML5 drag and drop, no library. The move is optimistic so the board
// responds at the speed of the gesture, then the server answers with its own
// render. Counts, open slots, unit strips, the mix row and the backlog header
// all change together on a drop, and recomputing them here would be a second
// implementation of the same arithmetic, free to disagree with the first.
//
// The grid and the rail are separate Turbo Stream targets and the server sends
// the rail only when a save moved an idea in or out of it, so a drag from one
// month to another leaves the rail alone. This controller sits on the wrapper
// around both, which is why it survives either one being replaced.
//
// Every drag has a non-drag equivalent in the idea sheet's month select, so
// nothing here is the only way to do anything.
export default class extends Controller {
  static targets = ["search", "clear", "rail", "section", "count", "card", "more", "noResults", "draggable", "row", "cell", "openSlot", "dropTarget", "unschedule", "toast", "recipeToggle"]
  static values = { planFrom: String, recipeFilter: { type: String, default: "all" } }

  // The rail's state lives here rather than in the markup, because the markup
  // is replaced out from under a still-connected controller every time a save
  // changes the backlog.
  initialize() {
    this.limits = new Map()
    this.railScroll = 0
    this.searchTerm = ""
  }

  // The recipe toggle lives on this wrapper, outside both Turbo Stream targets,
  // so it is restored once here rather than on every rail redraw.
  connect() {
    this.dragging = null
    this.restoreRecipeFilter()
    this.updateRecipeToggleState()
    this.syncRail()
  }

  // The rail is its own Turbo Stream target, so a save that touches the backlog
  // swaps this element for a fresh one. Everything the old rail was holding has
  // to be put back: how far it was scrolled, how far each section was expanded,
  // and the search being typed into it.
  railTargetConnected() {
    this.syncRail()
    this.railTarget.scrollTop = this.railScroll
  }

  syncRail() {
    this.restoreSearch()
    this.renderRail()
  }

  rememberScroll() {
    this.railScroll = this.railTarget.scrollTop
  }

  // A save that changes the backlog replaces the rail, which takes the search
  // box with it. The term is held on the controller so filtering the rail,
  // dragging one card out and carrying on does not mean retyping between every
  // card.
  //
  // It is deliberately not persisted past the page, which it used to be. A term
  // that outlives a reload is a filter nobody remembers switching on: one word
  // left in the box hid eight of nine cards, including ones that had just been
  // dragged off the board into the rail to look at.
  restoreSearch() {
    if (!this.hasSearchTarget) return

    this.searchTarget.value = this.searchTerm
  }

  // ── Recipe / Article toggle ─────────────────────────────────────────────────
  //
  // A three-way segmented control (All / Recipes / Articles) that composes
  // with the search box and the reveal cap rather than replacing them: a card
  // has to match the search and the kind, and still fall inside its section's
  // limit, to stay visible. Unlike the search term this one does persist, so
  // it survives the full page load a hub chip or the sort link costs: it is a
  // mode someone picks for a stretch of work, not a throwaway query.

  restoreRecipeFilter() {
    // Not hasRecipeFilterValue: Stimulus reports that as
    // `this.data.has(key) || definition.hasCustomDefaultValue`, and this value
    // declares a default, so it is true whether or not the server set it. It
    // always took this branch, which meant the stored choice was never read
    // and was overwritten with "all" on every page load. Ask the DOM instead.
    if (this.element.hasAttribute(RECIPE_FILTER_ATTRIBUTE)) {
      // The URL (and so the server render) already said what it should be,
      // e.g. a shared link with ?kind=recipes. Keep storage in step with it.
      this.persistRecipeFilter(this.recipeFilterValue)
      return
    }

    try {
      const saved = sessionStorage.getItem(RECIPE_FILTER_KEY)
      if (saved) this.recipeFilterValue = saved
    } catch (error) {
      // See restoreSearch.
    }
  }

  persistRecipeFilter(kind) {
    try {
      sessionStorage.setItem(RECIPE_FILTER_KEY, kind)
    } catch (error) {
      // See restoreSearch.
    }
  }

  selectRecipeFilter(event) {
    event.preventDefault()
    const kind = event.currentTarget.dataset.kind || "all"

    this.recipeFilterValue = kind
    this.persistRecipeFilter(kind)
    this.updateRecipeToggleState()
    this.renderRail()
  }

  updateRecipeToggleState() {
    if (!this.hasRecipeToggleTarget) return

    this.recipeToggleTarget.querySelectorAll("[data-kind]").forEach((button) => {
      button.classList.toggle("fg-planbar__segmentbtn--on", button.dataset.kind === this.recipeFilterValue)
    })
  }

  matchesRecipeFilter(element) {
    if (this.recipeFilterValue === "recipes") return element.dataset.isRecipe === "true"
    if (this.recipeFilterValue === "articles") return element.dataset.isRecipe === "false"

    return true
  }

  // ── Backlog rail filtering and paging ──────────────────────────────────────

  filter() {
    this.searchTerm = this.hasSearchTarget ? this.searchTarget.value : ""
    this.renderRail()
  }

  clearSearch() {
    this.searchTerm = ""

    if (this.hasSearchTarget) {
      this.searchTarget.value = ""
      this.searchTarget.focus()
    }

    this.renderRail()
  }

  // Which cards the rail shows: the ones matching both the search and the kind
  // toggle, capped at each section's limit.
  //
  // A search term lifts the cap, because paging through search results is not
  // searching and the idea being hunted for is as likely to be the hundredth in
  // the list as the first. The kind toggle does not lift it: unlike a search it
  // is a mode someone leaves switched on, and "Recipes" can still be more cards
  // than the rail wants to draw at once.
  renderRail() {
    const typed = this.hasSearchTarget ? this.searchTarget.value : ""
    const term = typed.trim().toLowerCase()
    const matched = this.sectionTargets.reduce(
      (total, section) => total + this.renderSection(section, term), 0
    )

    // Keyed off what is in the box, not the trimmed term: a box holding only
    // spaces filters nothing but still needs a way to be emptied.
    if (this.hasClearTarget) this.clearTarget.hidden = typed === ""

    if (this.hasNoResultsTarget) {
      const filtering = term !== "" || this.recipeFilterValue !== "all"
      this.noResultsTarget.hidden = !(filtering && matched === 0)
    }

    this.applyRecipeFilterToBoard()
  }

  // The grid's chips have no search box to compose with, so the recipe filter
  // is the only predicate that hides them.
  applyRecipeFilterToBoard() {
    this.element.querySelectorAll(".fg-chip[data-is-recipe]").forEach((chip) => {
      chip.hidden = !this.matchesRecipeFilter(chip)
    })
  }

  renderSection(section, term) {
    const limit = term === "" ? this.limitFor(section) : Infinity
    let matched = 0
    let shown = 0

    this.cardsIn(section).forEach((card) => {
      const match = (term === "" || (card.dataset.title || "").includes(term)) &&
        this.matchesRecipeFilter(card)
      if (match) matched += 1

      const visible = match && shown < limit
      if (visible) shown += 1
      card.hidden = !visible
    })

    const more = section.querySelector("[data-ideas-plan-target~='more']")
    if (more) {
      const rest = matched - shown
      more.hidden = rest <= 0
      more.textContent = rest > PAGE ? `Show ${PAGE} more (${rest} left)` : `Show ${rest} more`
    }

    // "3 of 9" while a filter is on. The header used to report the section's
    // full size no matter what was drawn beneath it, so a rail showing one card
    // under a heading that said nine read as a bug in the rail rather than as a
    // filter doing its job.
    const count = section.querySelector("[data-ideas-plan-target~='count']")
    if (count) {
      const total = Number(count.dataset.total || 0)
      count.textContent = matched < total ? `${matched} of ${total}` : String(total)
    }

    return matched
  }

  showMore(event) {
    const section = event.currentTarget.closest("[data-ideas-plan-target~='section']")
    if (!section) return

    this.limits.set(section.dataset.section, this.limitFor(section) + PAGE)
    this.renderRail()
  }

  limitFor(section) {
    return this.limits.get(section.dataset.section) || PAGE
  }

  cardsIn(section) {
    return Array.from(section.querySelectorAll("[data-ideas-plan-target~='card']"))
  }

  // ── Opening the sheet ──────────────────────────────────────────────────────
  //
  // The cards carry no anchor, so opening one is a frame navigation done by
  // hand. Setting src on the frame is what a turbo-frame link would have done
  // anyway, minus the link-drag behaviour that made the cards unreliable to
  // pick up.

  openSheet(event) {
    // The triage buttons live inside the card, so a click on one must not also
    // open the idea behind it.
    if (event.target.closest("button, form")) return

    const card = event.currentTarget
    const frame = document.getElementById("idea_sheet")
    if (!frame || !card.dataset.sheetUrl) return

    frame.src = card.dataset.sheetUrl
  }

  openSheetOnKey(event) {
    if (event.key !== "Enter" && event.key !== " ") return

    event.preventDefault()
    this.openSheet(event)
  }

  // ── Drag ───────────────────────────────────────────────────────────────────

  dragStart(event) {
    const source = event.target.closest("[data-ideas-plan-target~='draggable']")
    if (!source || source.dataset.accepted === "true") {
      event.preventDefault()
      return
    }

    // A drag that never reported its end leaves the board in drag state and
    // every later drag confused about what is moving. Clear it rather than
    // trusting dragend to have fired.
    if (this.dragging) this.dragEnd()

    this.dragging = {
      element: source,
      ideaId: source.dataset.ideaId,
      hubId: source.dataset.hubId || "",
      origin: source.parentElement,
      originNext: source.nextElementSibling
    }

    event.dataTransfer.effectAllowed = "move"
    // Firefox will not start a drag without payload, even when the payload is
    // never read back.
    event.dataTransfer.setData("text/plain", source.dataset.ideaId)

    source.classList.add("is-dragging")
    this.element.classList.add("is-dragging-board")
    this.markDroppableRows()

    // The drawer is a fixed column over the right of the board, so the cells
    // under it cannot be dropped on while it is up. A drag produces no click,
    // which is what the drawer dismisses itself on, so it would stay there for
    // the whole gesture with a third of the months unreachable. The sheet
    // closes itself: it owns its own teardown.
    this.dispatch("dragstart")
  }

  dragEnd() {
    this.dragging?.element.classList.remove("is-dragging")
    this.element.classList.remove("is-dragging-board")
    this.rowTargets.forEach((row) => {
      row.classList.remove("is-droppable")
      row.querySelector(".fg-rowhead__drop")?.classList.remove("fg-rowhead__drop--refile")
    })
    this.clearHover()
    this.dragging = null
  }

  // Every row accepts every idea. Landing a piece in the wrong hub is easy to
  // do and has to be easy to undo, and the rows are the only place the hub is
  // visible, so they are where an admin reaches to correct it. The hint says
  // which of the two moves a given row would make, so crossing hubs is a
  // choice rather than a surprise.
  markDroppableRows() {
    this.rowTargets.forEach((row) => {
      row.classList.add("is-droppable")

      const hint = row.querySelector(".fg-rowhead__drop")
      if (hint) {
        const crossing = this.hubOf(row) !== this.dragging.hubId
        hint.textContent = this.dropHintFor(row)
        hint.classList.toggle("fg-rowhead__drop--refile", crossing)
      }
    })
  }

  hubOf(row) {
    return row.dataset.hubId || ""
  }

  rowAccepts() {
    return true
  }

  dropHintFor(row) {
    const rowHub = this.hubOf(row)
    if (rowHub === this.dragging.hubId) return "Drop in this row"
    if (rowHub === "") return "Drop to unfile it"

    const name = row.querySelector(".fg-rowhead__name")?.textContent.trim()

    return name ? `Drop to file in ${name}` : "Drop to file it here"
  }

  dragOverCell(event) {
    const cell = event.target.closest("[data-ideas-plan-target~='cell']")
    if (!cell || !this.dragging) return
    if (!this.cellAccepts(cell)) return
    if (cell.contains(this.dragging.element)) return

    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
    this.showDropTarget(cell)
  }

  // Fires when crossing into a child of the cell too, so only a move that
  // actually left the cell counts.
  dragLeaveCell(event) {
    const cell = event.currentTarget
    if (event.relatedTarget && cell.contains(event.relatedTarget)) return
    if (this.hoveredCell === cell) this.clearHover()
  }

  dragOverUnschedule(event) {
    if (!this.dragging) return
    // An idea already in the backlog has no month to clear.
    if (!this.dragging.element.closest("[data-ideas-plan-target~='cell']")) return

    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
    this.unscheduleTarget.classList.add("is-over")
  }

  dragLeaveUnschedule() {
    this.unscheduleTarget.classList.remove("is-over")
  }

  showDropTarget(cell) {
    if (this.hoveredCell === cell) return
    this.clearHover()

    const count = Number(cell.dataset.count || 0) + 1
    const target = Number(cell.dataset.target || 0)
    const month = cell.dataset.monthLabel

    const slot = cell.querySelector("[data-ideas-plan-target~='dropTarget']")
    if (slot) {
      slot.textContent = target > 0
        ? `Release to fill ${month} · ${count} of ${target}`
        : `Release to place in ${month}`
      slot.hidden = false
    }

    // The drop target stands in for the first open slot rather than adding to
    // the stack, so the cell does not jump as you move across the row.
    const firstOpen = cell.querySelector(".fg-openslot:not([hidden])")
    if (firstOpen) {
      firstOpen.hidden = true
      this.hiddenSlot = firstOpen
    }

    cell.classList.add("is-over")
    this.hoveredCell = cell
  }

  clearHover() {
    if (this.hoveredCell) {
      this.hoveredCell.classList.remove("is-over")
      const slot = this.hoveredCell.querySelector("[data-ideas-plan-target~='dropTarget']")
      if (slot) slot.hidden = true
    }
    if (this.hiddenSlot) this.hiddenSlot.hidden = false

    this.hoveredCell = null
    this.hiddenSlot = null

    if (this.hasUnscheduleTarget) this.unscheduleTarget.classList.remove("is-over")
  }

  dropOnCell(event) {
    const cell = event.target.closest("[data-ideas-plan-target~='cell']")
    if (!cell || !this.dragging) return
    if (!this.cellAccepts(cell)) return

    event.preventDefault()
    const month = cell.dataset.month
    // Filing and scheduling are one gesture, so they are one save. The hub only
    // rides along when the drop actually crosses rows, so a move within a row
    // never rewrites a field it did not touch.
    const cellHub = cell.dataset.hubId || ""
    const hubId = cellHub === this.dragging.hubId ? null : cellHub

    this.clearHover()
    this.moveOptimistically(cell)
    this.save(month, hubId)
  }

  cellAccepts(cell) {
    return Boolean(cell.closest("[data-ideas-plan-target~='row']"))
  }

  dropOnUnschedule(event) {
    if (!this.dragging) return
    if (!this.dragging.element.closest("[data-ideas-plan-target~='cell']")) return

    event.preventDefault()
    this.clearHover()
    // Unscheduling clears the month only. An idea's hub is what it is about,
    // and pulling it off the calendar does not change that.
    this.save("", null)
  }

  // Put the chip where it was released before the request goes out, so the
  // board answers the gesture immediately. The server's render replaces this
  // a moment later with the authoritative counts.
  moveOptimistically(cell) {
    const footer = cell.querySelector(".fg-cell__foot")
    cell.insertBefore(this.dragging.element, footer)
  }

  async save(month, hubId = null) {
    const { ideaId, element, origin, originNext } = this.dragging

    const body = new FormData()
    body.append("idea[workorder_month]", month)
    if (hubId !== null) body.append("idea[hub_id]", hubId)
    body.append("from_plan", "1")
    body.append("plan_from", this.planFromValue)
    if (this.recipeFilterValue && this.recipeFilterValue !== "all") {
      body.append("kind", this.recipeFilterValue)
    }
    body.append("_method", "patch")

    try {
      const response = await fetch(`/contributors/ideas/${ideaId}`, {
        method: "POST",
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-CSRF-Token": this.csrfToken
        },
        body,
        credentials: "same-origin"
      })

      if (!response.ok) throw new Error(`Save failed (${response.status})`)

      window.Turbo.renderStreamMessage(await response.text())
    } catch (error) {
      // Put it back exactly where it came from, then say so. A silently
      // reverted drag looks like the board ignored you.
      origin.insertBefore(element, originNext)
      this.showToast("That move did not save. The idea is back where it was.")
      console.error(error)
    }
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }

  showToast(message) {
    if (!this.hasToastTarget) return

    this.toastTarget.textContent = message
    this.toastTarget.hidden = false
    clearTimeout(this.toastTimer)
    this.toastTimer = setTimeout(() => { this.toastTarget.hidden = true }, 6000)
  }
}
